import BannersContainer from 'components/ads/BannersContainer'
import Header from 'components/landing/Header'
import PageMeta from 'components/base/PageMeta'
import { PromoCodeContext } from 'components/landing/lib/promoCodeContext'
import { HostType } from 'configs/theme'
import { FC } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { withUserProps } from 'lib/contextCreation'
import { PromoCode } from 'lib/dto'
import { GetServerSideProps } from 'next'
import { setCookie } from 'nookies'
import SchemaMarkup from 'components/base/SchemaMarkup'
import { useEffect, useState } from 'react'
import { useIntl } from 'lib/intl-utils'
import cookieParser from 'cookie'
import { plansByHost, Plan } from 'configs/pricing'
import SellematicsHero from 'components/landing/sellematics/blocks/SellematicsHero'
import SellematicsIndexPage from 'components/landing/sellematics/SellematicsIndexPage'
import SellmonitorHero from 'components/landing/sellmonitor/blocks/SellmonitorHero'
import SellmonitorIndexPage from 'components/landing/sellmonitor/SellmonitorIndexPage'
import SellscreenIndexPage from 'components/landing/sellscreen/SellscreenIndexPage'
import OtherHostIndexPage from 'components/landing/sellmonitor/OtherHostIndexPage'
import { contentByHost } from 'components/landing/sellmonitor/blocks/Testimonials'
import { faqsByHost } from 'components/landing/sellmonitor/blocks/Faq'
import { getOrganizationSchema, getReviewSchema, getFAQPageSchema } from 'lib/schema-utils'
import { getCanonicalHref } from 'lib/canonical'

const metaByHost: Partial<
  Record<HostType, Partial<Record<string, { title: string; description: string }>>>
> = {
  sellmonitor: {
    ru: {
      title:
        'Sellmonitor - Сервис управления магазинами на маркетплейсах | Плагин для внутренней аналитики, анализа, мониторинга конкурентов, трендов и цен',
      description:
        '🚀 Сервис управления магазинами Sellmonitor на маркетплейсах Wildberries, Ozon и Яндекс Маркет. Отслеживайте конкурентов и тренды продаж, анализируйте товары, цены и спрос, чтобы увеличить прибыль и управлять бизнесом из одного кабинета!',
    },
  },
  uzum: {
    uz: {
      title: 'Sellmonitor marketpleys tahlili xizmati savdo va trendlarni tahlil qilish uchun',
      description:
        '🚀 Bizning marketpleys tahlili xizmatimiz bilan savdolaringizni oshiring! Raqiblarning narxlarini kuzatish, trendlar va mahsulotlarni tahlil qilish — bularning barchasi biznesingiz muvaffaqiyati uchun. Kiring va bugundan o‘sishni boshlang!',
    },
    ru: {
      title: 'Сервис аналитики маркетплейсов Sellmonitor для анализа продаж и трендов',
      description:
        '🚀 Повысьте свои продажи с нашим сервисом аналитики маркетплейсов! Отслеживание цен конкурентов, анализ трендов и товаров — все для успеха вашего бизнеса. Заходите и начинайте расти уже сегодня!',
    },
  },
  sellscreen: {
    en: {
      title: 'Sellscreen marketplace analytics service for sales and trends analysis',
      description:
        '🚀 Boost your sales with our marketplace analytics service! Track competitor prices, analyze trends and products — everything for your business success. Sign up and start growing today!',
    },
    zh: {
      title: 'Sellscreen市场分析服务，用于销售和趋势分析',
      description:
        '🚀 通过我们的市场分析服务提升您的销售业绩！追踪竞争对手价格、分析趋势和产品 — 为您的商业成功提供一切所需。立即访问我们，今天就开始成长！',
    },
  },
  sellematics: {
    ru: {
      title: 'Sellematics — e-commerce аналитика и исследования',
      description: 'Сервис аналитики 20+ маркетплейсов. Покажем всё, что нужно вашему бизнесу',
    },
    en: {
      title: 'Sellematics — e-commerce analytics and research',
      description:
        'Analytics service for 20+ e-commerce retailers. Gives you all the data your business needs',
    },
  },
}

export const getServerSideProps: GetServerSideProps = withUserProps(
  async ({ pageContext, userContext }) => {
    if (userContext.theme.hostType === 'psb') {
      return {
        redirect: {
          destination: '/oauth/start/psb/',
          permanent: true,
        },
      }
    }

    if (userContext.theme.hostType === 'vtb') {
      if (pageContext.query.utm_source === 'crm_vtb') {
        return {
          redirect: {
            destination: '/login/?utm_source=crm_vtb',
            permanent: true,
          },
        }
      }
      return {
        redirect: {
          destination: '/login/',
          permanent: true,
        },
      }
    }

    if (userContext.theme.hostType === 'mayak') {
      const promoCodeFromQuery =
        typeof pageContext.query.promoCode === 'string' ? pageContext.query.promoCode.trim() : null
      const promoCode = promoCodeFromQuery || pageContext.req.cookies['promoCode']

      if (pageContext.query.promoCode === '') {
        setCookie(pageContext, 'promoCode', '', {
          maxAge: 7 * 24 * 60 * 60,
          path: '/',
        })
      } else if (promoCode) {
        await userContext
          .getPromoCodeSignup(promoCode)
          .then((data) => {
            if (!Array.isArray(data) && promoCodeFromQuery) {
              setCookie(pageContext, 'promoCode', promoCodeFromQuery, {
                maxAge: 7 * 24 * 60 * 60,
                path: '/',
              })
            }
          })
          .catch(() => {})
      }

      return {
        redirect: {
          destination: 'https://mayak.bz',
          permanent: true,
        },
      }
    }

    return {
      props: {
        isResponsive: true,
      },
    }
  },
  { forceUnauthorizedMode: true }
)

const LandingPage: FC = () => {
  const ctx = useUserContext()
  const { lang } = useIntl()
  const [promoCode, setPromoCode] = useState<PromoCode>()
  const [hostname, setHostname] = useState<string>('')

  useEffect(() => {
    setHostname(window.location.hostname)
    const cookies = cookieParser.parse(document.cookie)

    const params = new URLSearchParams(window.location.search)
    const code = params.get('promoCode') ?? cookies['promoCode']

    if (code != null) {
      setCookie(null, 'promoCode', code, {
        maxAge: 7 * 24 * 60 * 60,
        path: '/',
      })
      ctx
        .getPromoCodeSignup(code)
        .then((data) => {
          if (!Array.isArray(data)) setPromoCode(data)
        })
        .catch(() => {})
    }

    const utmParams = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'actionpay',
    ]
    utmParams.forEach((param) => {
      const value = params.get(param)
      if (value !== null) {
        setCookie(null, param, value, {
          maxAge: 30 * 24 * 60 * 60,
          path: '/',
        })
      }
    })
  }, [])

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: ctx.theme.title,
    url: `https://${hostname}`,
  }

  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: ctx.theme.title,
    operatingSystem: 'Web',
    applicationCategory: 'BusinessApplication',
    ...(plansByHost[ctx.theme.hostType] && {
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: plansByHost[ctx.theme.hostType]!.currency,
        lowPrice: Math.min(
          ...plansByHost[ctx.theme.hostType]!.plans.map(
            (plan: Plan) => Object.values(plan.pricing)[0].discountedPrice
          )
        ),
        highPrice: Math.max(
          ...plansByHost[ctx.theme.hostType]!.plans.map((plan: Plan) => {
            const priceList = Object.values(plan.pricing)
            return priceList[priceList.length - 1].discountedPrice
          })
        ),
        offerCount: plansByHost[ctx.theme.hostType]!.plans.length,
      },
    }),
  }

  let meta = metaByHost[ctx.theme.hostType]![lang]!

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: ctx.theme.title,
    description: meta.description,
    brand: {
      '@type': 'Brand',
      name: ctx.theme.title,
    },
    ...(plansByHost[ctx.theme.hostType] && {
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: plansByHost[ctx.theme.hostType]!.currency,
        lowPrice: Math.min(
          ...plansByHost[ctx.theme.hostType]!.plans.map(
            (plan: Plan) => Object.values(plan.pricing)[0].discountedPrice
          )
        ),
        highPrice: Math.max(
          ...plansByHost[ctx.theme.hostType]!.plans.map((plan: Plan) => {
            const priceList = Object.values(plan.pricing)
            return priceList[priceList.length - 1].discountedPrice
          })
        ),
        offerCount: plansByHost[ctx.theme.hostType]!.plans.length,
      },
    }),
  }

  const reviews = contentByHost[ctx.theme.hostType]?.[lang]
  const faqs = faqsByHost[ctx.theme.hostType]?.[lang]
  const organizationSchema = getOrganizationSchema(ctx)
  const reviewSchema = reviews ? getReviewSchema(ctx, reviews) : null
  const faqPageSchema = faqs ? getFAQPageSchema(faqs) : null
  const canonicalHref = getCanonicalHref(ctx.theme.canonicalURL, '/')

  return (
    <PromoCodeContext.Provider value={promoCode ?? null}>
      <article className="">
        <PageMeta title={meta.title} description={meta.description} canonical={canonicalHref} />
        <SchemaMarkup schema={websiteSchema} id="website-schema" />
        <SchemaMarkup schema={organizationSchema} id="organization-schema" />
        <SchemaMarkup schema={softwareSchema} id="software-schema" />
        <SchemaMarkup schema={productSchema} id="product-schema" />
        {reviewSchema && <SchemaMarkup schema={reviewSchema} id="review-schema" />}
        {faqPageSchema && <SchemaMarkup schema={faqPageSchema} id="faq-page-schema" />}
        {['sellmonitor', 'sellscreen'].includes(ctx.theme.hostType) ? (
          <>
            <BannersContainer scope="landing" placement="index" />
            <Header />
            <SellmonitorHero />
          </>
        ) : (
          <div className="min-h-[calc(min(100vh,768px))]">
            <BannersContainer scope="landing" placement="index" />
            <Header />
            {ctx.theme.hostType === 'sellematics' ? <SellematicsHero /> : <SellmonitorHero />}
          </div>
        )}
        <SellematicsIndexPage />
        <SellmonitorIndexPage />
        <SellscreenIndexPage />
        <OtherHostIndexPage />
      </article>
    </PromoCodeContext.Provider>
  )
}

export default LandingPage
